import {
	ChangeDetectionStrategy,
	Component,
	forwardRef,
	inject,
	OnDestroy,
	OnInit,
	signal,
	untracked,
} from '@angular/core';
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CATEGORIES_API } from '../../../api/injection-tokens/services.token';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatOption } from '@angular/material/autocomplete';
import { ENVIRONMENT } from '../../../../environments/environment.interface';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LvrField } from '../field/field';
import { TranslocoModule } from '@jsverse/transloco';
import { CategoriesApi } from '../../../api/services/categories.service';

@Component({
	selector: 'lvr-branch-field',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatFormFieldModule,
		MatAutocompleteModule,
		TranslocoModule,
		ReactiveFormsModule,
		MatOption,
		MatInputModule,
		LvrField,
	],
	providers: [
		{
			provide: CATEGORIES_API,
			useClass: CategoriesApi,
		},
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => BranchField),
			multi: true,
		},
	],
	templateUrl: './branch-field.html',
	styleUrl: './branch-field.scss',
})
export class BranchField implements ControlValueAccessor, OnInit, OnDestroy {
	public readonly form = inject(FormBuilder).group({
		industryName: [''],
	});

	public readonly environment = inject(ENVIRONMENT);
	private readonly offset = this.environment.pagination.offset;
	private readonly range = this.environment.pagination.range;

	private categoryService = inject(CATEGORIES_API);
	private _value: string | null = null;

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onChange = (_value: string | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onTouched = () => {};

	private _isDisabled = false;

	public get disabled() {
		return this._isDisabled;
	}

	public suggestions = signal<string[]>([]);

	private readonly displayBranchFields = signal<boolean>(false);
	private readonly debounceTimeout = signal<number | undefined>(undefined);

	private _destroy = new Subject<void>();

	public ngOnInit(): void {
		this.registerValueChanges();
	}

	public writeValue(obj: string | null) {
		this._value = obj;
		this._onChange(obj);
		this.form.patchValue({ industryName: obj ?? '' }, { emitEvent: false });
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public registerOnChange(fn: any): void {
		this._onChange = fn;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public registerOnTouched(fn: any): void {
		this._onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this._isDisabled = isDisabled;
	}

	public ngOnDestroy(): void {
		this._destroy.next();
		this._destroy.complete();
		if (untracked(this.debounceTimeout)) {
			clearTimeout(untracked(this.debounceTimeout));
		}
	}

	private registerValueChanges() {
		this.form.valueChanges.pipe(takeUntil(this._destroy), distinctUntilChanged()).subscribe((value) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const branch = (value as any).industryName ?? '';

			this.updateSuggestions(branch);
			this.writeValue(branch);
		});
	}

	public displayBranch(branch: string) {
		if (!branch) return '';
		return branch;
	}

	private async updateSuggestions(branch: string) {
		const search = `${branch}`;
		if (!search) {
			const industries = (await this.categoryService.getAllIndustries(this.offset, this.range)).entities;
			this.suggestions.set(industries);
			return;
		}

		const delay = 300;
		if (untracked(this.debounceTimeout)) {
			clearTimeout(untracked(this.debounceTimeout));
		}

		const timeout = window.setTimeout(async () => {
			const industries = (await this.categoryService.getAllIndustries(this.offset, this.range, search)).entities;
			this.suggestions.set(industries);
		}, delay);
		this.debounceTimeout.set(timeout);
	}

	public branchSelected(event: MatAutocompleteSelectedEvent) {
		const selected = event.option.value as string;
		this.form.patchValue({ industryName: selected });
		this.suggestions.set([]);
	}

	public onInputFocus() {
		const branch = this.form.get('industryName')?.value ?? '';
		if (!branch.trim()) {
			this.updateSuggestions('');
		}
	}
}
